import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { PersonalizationFormData } from "../schemas";
import { useUploadDoc } from "@/services/queries/usePerson";

export function Personalization({ prefix, form }:
  { prefix: "user" | "company"; form: UseFormReturn<PersonalizationFormData> }) {

  const nameField: keyof PersonalizationFormData = `${prefix}ProfileImage`
  const [preview, setPreview] = useState<string | null>(localStorage.getItem(nameField) || null);

  const { mutateAsync: uploadDoc } = useUploadDoc();

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'url_document');

      return await uploadDoc({
        data: formData
      });
    } catch (error) {
      console.error('Erro ao fazer upload do documento:', error);
    }
  };

  const ProfileImageUpload = ({ name }: { name: keyof PersonalizationFormData }) => {
    
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      // let url
      const file = event.target.files?.[0];
      if (file) {
        await handleFileUpload(file)
          // .then(r => { url = r?.url })

        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
          localStorage.setItem(nameField, reader.result as string)
        };
        reader.readAsDataURL(file);
        form.setValue(name, `https://www.app.cineblocks.com.br/`);
      }
    };

    const removeImage = () => {
      setPreview(null);
      localStorage.removeItem(nameField)
      form.setValue(name, null);
    };

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem className="border-2 border-gray-600 border-dashed p-5 rounded-md">
            <FormLabel />
            <FormControl>
              <div className="flex items-center gap-4">
                {preview && (
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-600 flex items-center justify-center">
                      <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-1 -right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-2 h-2" />
                    </button>
                  </div>
                )}
                {!preview && (
                  <div className="w-20 h-20 rounded-full bg-gray-600 flex items-center justify-center">
                    <div className="w-14 h-14 bg-gray-400 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer bg-gray-50 w-8 h-8 hover:bg-gray-200 text-blue-700 rounded-full text-sm flex items-center justify-center gap-2">
                    <Upload className="w-4 h-4" />
                    <input
                      ref={field.ref}
                      type="file"
                      name={field.name}
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>

                  <div className="flex flex-col items-start">
                    <span className="text-xs font-medium">
                      Alterar foto
                    </span>
                    <span className="text-xs text-gray-400">Clique para trocar seu arquivo</span>
                  </div>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Name */}
        <FormField
          control={form.control}
          name={`${prefix}Name`}
          render={({ field }) => (
            <FormItem className="max-md:col-span-2">
              <FormLabel className="text-white">
                {prefix === "user" ? "Seu nome de usuário" : "Nome da empresa"}
              </FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description */}
        <FormField
          control={form.control}
          name={`${prefix}Description`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-white">Descrição / Biografia</FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="mt-6 lg:flex items-center gap-6">
        {/* Profile Image */}
        <div className="flex flex-col gap-4">
          <Label className=" text-white">Imagem de Perfil</Label>
          <ProfileImageUpload name={nameField} />
        </div>

        {/* Social Media Links */}
        <div className="grow grid grid-cols-1 md:grid-cols-2 max-lg:mt-6 gap-6">
          {/* Facebook */}
          <FormField
            control={form.control}
            name={`${prefix}FacebookUrl`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Facebook</FormLabel>
                <FormControl>
                  <Input placeholder="https://www.facebook.com/" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* X (Twitter) */}
          <FormField
            control={form.control}
            name={`${prefix}TwitterUrl`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">X</FormLabel>
                <FormControl>
                  <Input placeholder="https://x.com/" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* LinkedIn */}
          <FormField
            control={form.control}
            name={`${prefix}LinkedinUrl`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">LinkedIn</FormLabel>
                <FormControl>
                  <Input placeholder="https://www.linkedin.com/" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Instagram */}
          <FormField
            control={form.control}
            name={`${prefix}InstagramUrl`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">Instagram</FormLabel>
                <FormControl>
                  <Input placeholder="@usuário" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  )
}